import { useMemo } from 'react';
import { Spin } from 'antd';
import AddOnsTable from './AddOnsTable';
import ConditionalRender from '../../../../shared/components/ConditionalRender';
import { useClientDetails } from '../../../../hooks/useClientDetails';
import { EMPTY_COPY } from '../../constants';
import type { ClientAddOn, ClientRecord } from '../../../../types/ClientManagementTypes';
import styles from './ClientTables.module.scss';

interface ClientAddOnsPanelProps {
  client: ClientRecord;
  /** Story 2: an add-on row opens the same Subscription page the pill does. */
  onOpenSubscription: () => void;
}

/**
 * The listing carries no add-ons, so an expanded row fetches the client's full
 * record. Mounted only while expanded, which is what keeps the fetch lazy.
 */
const ClientAddOnsPanel = ({ client, onOpenSubscription }: ClientAddOnsPanelProps) => {
  const { clientDetails, isLoadingClientDetails, isClientDetailsLoadingError } = useClientDetails(client.id, true);

  // The detail payload bills add-ons with the client, not per add-on, so the
  // row's status is the client's.
  const addOns: ClientAddOn[] = useMemo(
    () =>
      (clientDetails?.addons ?? []).map((addOn) => ({
        id: addOn.id,
        addonId: addOn.addonId,
        name: addOn.name,
        addonType: addOn.addonType,
        expiration: addOn.expiresAt,
        cost: addOn.price,
        status: client.status,
      })),
    [clientDetails, client.status],
  );

  if (isLoadingClientDetails) {
    return (
      <div className={styles.addOnsLoading}>
        <Spin size="small" />
      </div>
    );
  }

  return (
    <ConditionalRender visible={!isClientDetailsLoadingError} fallback={<p className={styles.addOnsErrorState}>{EMPTY_COPY.ADD_ONS_ERROR}</p>}>
      <AddOnsTable addOns={addOns} onSelect={onOpenSubscription} />
    </ConditionalRender>
  );
};

export default ClientAddOnsPanel;
